import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Info } from 'lucide-react';

export const ScoreInfoDialog = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="score-info-btn">
          <Info className="h-4 w-4" />
          How Scores Are Calculated
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="score-info-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl">Methodological Impact Index Calculation</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          {/* Overall Formula */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Overall Formula</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-center text-xl font-bold text-blue-900">
                  Impact Index = Evidence Score + Visibility Score (max 30)
                </p>
                <p className="text-center text-sm text-blue-700 mt-2">
                  Evidence Score depends on article type: Methodology (max 70), ESS (max 35), or Clinical Value (max 35)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Three Scoring Paths */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Three Scoring Systems</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">Each article is classified into one of three categories. Only one scoring system applies per article.</p>
            </CardContent>
          </Card>

          {/* Methodology Score */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">1. Methodology Score (Maximum 70 points)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-green-50 p-3 rounded border border-green-200 mb-4">
                <p className="text-sm text-green-900">
                  <strong>Applies to:</strong> RCT, Prospective cohort, Retrospective cohort, Case-control, Cross-sectional studies.
                  AI-powered analysis extracts study features, then a deterministic formula calculates the score.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Study Design (Max 30 points)</h4>
                <ul className="space-y-1 text-sm ml-4">
                  <li>RCT: <strong>28 points</strong></li>
                  <li>Prospective cohort: <strong>22 points</strong></li>
                  <li>Case-control: <strong>18 points</strong></li>
                  <li>Retrospective cohort: <strong>15 points</strong></li>
                  <li>Cross-sectional: <strong>10 points</strong></li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Sample Size (Max 15 points)</h4>
                <ul className="space-y-1 text-sm ml-4">
                  <li>&ge; 5000 participants: <strong>15</strong></li>
                  <li>1000-4999: <strong>12</strong></li>
                  <li>500-999: <strong>9</strong></li>
                  <li>100-499: <strong>6</strong></li>
                  <li>&lt; 100: <strong>3</strong></li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Follow-up Duration (Max 15 points)</h4>
                <ul className="space-y-1 text-sm ml-4">
                  <li>&ge; 60 months: <strong>15</strong> | 24-59: <strong>12</strong> | 12-23: <strong>9</strong> | 6-11: <strong>6</strong> | &lt;6: <strong>3</strong></li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Methodological Rigor (Max 10 points)</h4>
                <ul className="space-y-1 text-sm ml-4">
                  <li>Randomization: +3 | Blinding: +2 | Multicenter: +2 | Power calc: +2 | Primary endpoint: +1</li>
                </ul>
              </div>

              <div className="bg-emerald-50 p-3 rounded border border-emerald-200 mt-4">
                <p className="text-sm text-emerald-900">
                  <strong>Data Integrity Multiplier:</strong> Final = Raw Score x max(0.5, Integrity/100). AI checks statistical consistency and reporting transparency.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Evidence Synthesis Score */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">2. Evidence Synthesis Score — ESS (Maximum 35 points)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-teal-50 p-3 rounded border border-teal-200 mb-4">
                <p className="text-sm text-teal-900">
                  <strong>Applies to:</strong> Systematic reviews, meta-analyses, scoping reviews, narrative reviews, and critical appraisal reviews.
                  Combines a deterministic Review Design score with AI-evaluated transparency and clinical relevance.
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Review Design Score (0-15) — Deterministic</h4>
                <ul className="space-y-1 text-sm ml-4">
                  <li>Systematic review with meta-analysis: <strong>15</strong></li>
                  <li>Systematic review (no meta-analysis): <strong>12</strong></li>
                  <li>Scoping review: <strong>10</strong></li>
                  <li>Critical appraisal review: <strong>8</strong></li>
                  <li>Narrative review: <strong>6</strong></li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Transparency Score (0-10) — AI-evaluated, adaptive per type</h4>
                <p className="text-xs text-slate-500 mb-2">Each review type has specific criteria (e.g., PRISMA for systematic reviews, search strategy for scoping reviews)</p>
                <ul className="space-y-1 text-sm ml-4">
                  <li>Reporting completeness: up to +3</li>
                  <li>Search/methodology rigor: up to +2</li>
                  <li>Criteria/framework: up to +2</li>
                  <li>Analysis/evidence mapping: up to +2</li>
                  <li>Protocol/registration: up to +1</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Synthesis Structure Score (0-5)</h4>
                <ul className="space-y-1 text-sm ml-4">
                  <li>Structured synthesis across studies: <strong>5</strong></li>
                  <li>Partially structured: <strong>3</strong></li>
                  <li>Descriptive summary only: <strong>1</strong></li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Clinical Relevance Score (0-5)</h4>
                <ul className="space-y-1 text-sm ml-4">
                  <li>Guideline-informing: <strong>5</strong></li>
                  <li>High clinical relevance: <strong>4</strong></li>
                  <li>Moderate (subspecialty-specific): <strong>2</strong></li>
                  <li>Low (conceptual/theoretical): <strong>1</strong></li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Clinical Value Score */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">3. Clinical Value Score for Case Reports (Maximum 35 points)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-purple-50 p-3 rounded border border-purple-200 mb-4">
                <p className="text-sm text-purple-900">
                  <strong>Applies to:</strong> Case reports and case series. Uses Clinical Learning Value (0-10) x 3.5 multiplier.
                </p>
              </div>
              <div className="space-y-2 text-sm">
                <div><strong>Documentation Quality (0-3):</strong> Completeness of clinical description, workup, treatment</div>
                <div><strong>Novelty/Rarity (0-3):</strong> Uniqueness of the case, rare condition</div>
                <div><strong>Educational Value (0-2):</strong> Teaching potential for clinical practice</div>
                <div><strong>Follow-up & Outcome (0-2):</strong> Quality and duration of outcome reporting</div>
              </div>
              <p className="text-sm mt-3"><strong>Conversion:</strong> Clinical Learning Value (0-10) x 3.5 = Clinical Value Score (0-35)</p>
            </CardContent>
          </Card>

          {/* Visibility Score */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Visibility Score (Maximum 30 points) — All Types</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-sm ml-4">
                <li>Citations via CrossRef/OpenCitations (max 10 pts)</li>
                <li>PDF Downloads (max 8 pts)</li>
                <li>Altmetric Score (max 7 pts)</li>
                <li>PlumX Usage & Captures (max 3 pts)</li>
                <li>Publication Recency Bonus (max 2 pts)</li>
              </ul>
            </CardContent>
          </Card>

          {/* Evidence Levels */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Evidence Levels</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold mr-2 mt-0.5">Level I</span>
                  <span>RCT, Meta-analysis, Systematic review</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold mr-2 mt-0.5">Level II</span>
                  <span>Prospective cohort, Scoping reviews</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-semibold mr-2 mt-0.5">Level III</span>
                  <span>Retrospective cohort, Case-control, Critical appraisal reviews</span>
                </li>
                <li className="flex items-start">
                  <span className="inline-block bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-semibold mr-2 mt-0.5">Level IV</span>
                  <span>Narrative reviews, Case reports, Other</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AboutSystemDialog = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid="about-system-btn">
          About the System
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="about-system-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl">About Evidentica</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">System Developer</h3>
                  <p className="text-lg text-slate-700">Dr. Ivan Inkov</p>
                </div>
                
                <div className="border-t pt-4">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Ownership</h3>
                  <p className="text-lg text-slate-700 font-semibold">International Sci Ink Press Ltd.</p>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm text-slate-600">
                    © {new Date().getFullYear()} International Sci Ink Press Ltd. All rights reserved.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-2 text-blue-900">About the System</h3>
              <p className="text-sm text-blue-800 mb-3">
                Evidentica is an advanced AI-powered platform that evaluates medical research using three scoring systems: 
                Methodology Score (0-70) for primary clinical research, Evidence Synthesis Score (0-35) for review articles, 
                and Clinical Value Score (0-35) for case reports. Each system combines with a Visibility Score (0-30) 
                for the final Impact Index.
              </p>
              <h4 className="font-semibold mb-2 text-blue-900">AI Research Analysis</h4>
              <p className="text-sm text-blue-800">
                Our cutting-edge AI system (powered by GPT-5.2) performs comprehensive analysis of medical research 
                articles, extracting structured scientific knowledge including study design assessment, PICO framework, 
                methodological strengths and limitations, research gaps identification, and future research directions. 
                The system helps researchers quickly determine if an article is worth reading, citing, or if it may 
                inspire new research.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-6">
              <h3 className="font-semibold mb-2 text-amber-900">Important Disclaimer</h3>
              <p className="text-sm text-amber-800">
                The Impact Index is a methodological indicator designed to assess study design strength and research 
                visibility. It does not represent clinical truth, guideline recommendations, or treatment standards. 
                This tool is for research evaluation purposes only.
              </p>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};
